import { IOrder } from '@/storePinia/stores/thanks/interfaces/IOrder';
import { IBasket, IBasketProduct } from '@/storePinia/stores/basket/interfaces/IBasket';
import { IProductDetail } from '@/storePinia/stores/product/interfaces/IProductDetail';
import { ESPUTNIK_ACCOUNT_ID } from '@/settings/keys';

interface ISputnikProduct {
  productKey: string;
  price: string;
  isInStock: number;
  currency: string;
  quantity?: string;
}

type ESProduct = IBasketProduct | (IProductDetail & { count?: number });

const getId = (product: ESProduct) => product?.offer?.id || product?.id;

class ESputnik {
  #initPromise?: Promise<void>;

  init(): Promise<void> {
    if (this.#initPromise) {
      return this.#initPromise;
    }

    const existingScript = document.querySelector<HTMLScriptElement>(
      `script[src="https://statics.esputnik.com/scripts/${ESPUTNIK_ACCOUNT_ID}.js"]`
    );

    if (existingScript?.dataset.loaded === 'true') {
      return Promise.resolve();
    }

    this.#initPromise = new Promise<void>((resolve, reject) => {
      if (!window.eS) {
        const queue = ((...args: unknown[]): void => {
          queue.q?.push([args]);
        }) as ESputnikFunction;

        queue.q = [];
        window.eS = queue;

        queue('init');
      }

      if (existingScript) {
        existingScript.addEventListener(
          'load',
          () => {
            existingScript.dataset.loaded = 'true';
            resolve();
          },
          { once: true }
        );

        existingScript.addEventListener(
          'error',
          () => {
            this.#initPromise = undefined;
            reject(new Error('Failed to load eSputnik script'));
          },
          { once: true }
        );

        return;
      }

      const script = document.createElement('script');

      script.async = true;
      script.src = `https://statics.esputnik.com/scripts/${ESPUTNIK_ACCOUNT_ID}.js`;

      script.addEventListener(
        'load',
        () => {
          script.dataset.loaded = 'true';
          resolve();
        },
        { once: true }
      );

      script.addEventListener(
        'error',
        () => {
          this.#initPromise = undefined;
          reject(new Error('Failed to load eSputnik script'));
        },
        { once: true }
      );

      document.head.appendChild(script);
    });

    return this.#initPromise;
  }

  async #sendEvent(event: string, payload: Record<string, unknown>) {
    await this.init();

    window.eS?.('sendEvent', event, payload);
  }

  #formatItem(product: ESProduct): ISputnikProduct {
    const EProduct: ISputnikProduct = {
      productKey: getId(product).toString(),
      price: product.price?.price?.toString(),
      isInStock: +product.isAvailable,
      currency: 'UAH',
    };

    if (product.count) {
      EProduct.quantity = product.count.toString();
    }

    return EProduct;
  }

  #formatItems(items: ESProduct[]): Array<ISputnikProduct> {
    if (!items) return [];
    return items.map(this.#formatItem);
  }

  productPage(product: IProductDetail) {
    if (product) {
      this.#sendEvent('ProductPage', {
        ProductPage: this.#formatItem(product),
      });
    }
  }

  statusCart(basketData: IBasket) {
    if (basketData) {
      this.#sendEvent('StatusCart', {
        StatusCart: this.#formatItems(basketData.items as Array<IBasketProduct>),
        GUID: basketData.guid,
      });
    }
  }

  categoryPage(key: string) {
    if (key) {
      this.#sendEvent('CategoryPage', {
        CategoryPage: {
          categoryKey: key,
        },
      });
    }
  }

  purchasedItems(items: Array<IBasketProduct>, orderData: Partial<IOrder>) {
    this.#sendEvent('PurchasedItems', {
      OrderNumber: orderData.id,
      PurchasedItems: this.#formatItems(items),
      GUID: orderData.guid,
    });
  }

  // eslint-disable-next-line camelcase
  customerData(customerData: { user_phone: string; user_name?: string; user_email: string }) {
    this.#sendEvent('CustomerData', {
      CustomerData: customerData,
      //   {
      //   externalCustomerId: '1234509876',
      //   user_email: 'example@email.com',
      //   user_name: 'Johny',
      //   user_card_id: '321',
      //   user_phone: '3801111111111',
      //   user_city: 'Gdańsk',
      //   user_tags_gender: 'male',
      // },
    });
  }

  searchRequest(query: string) {
    this.#sendEvent('SearchRequest', {
      SearchRequest: {
        search: query,
        isFound: 0,
      },
    });
  }

  addToWishlist(product: IProductDetail) {
    this.#sendEvent('AddToWishlist', {
      AddToWishlist: this.#formatItem(product),
    });
  }
}

export default new ESputnik();
